import { useState } from 'react';
import PageMeta from '@/components/base/PageMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9pcvj8senbniolest40';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message
          || (parsed as { message?: string })?.message
          || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接');
      setFormStatus('error');
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '尾巴漫画',
    url: typeof window !== 'undefined' ? window.location.origin : '',
    description: '尾巴漫画汇聚无删减韩国漫画，下拉式免费韩漫大全在线阅读平台。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@weibamanga.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '望京SOHO',
      addressCountry: 'CN',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '尾巴漫画上的漫画是否完全免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，尾巴漫画上的所有韩国漫画均为完全免费阅读，无需付费订阅或购买。我们致力于为韩漫爱好者提供便捷的免费阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画更新频率是怎样的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '热门连载漫画通常在韩国更新后24小时内同步上线，确保读者能够第一时间阅读到最新内容。具体更新频率因作品而异，连载作品一般每周更新一话。',
        },
      },
      {
        '@type': 'Question',
        name: '什么是下拉式阅读？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '下拉式阅读是指无需翻页，只需向下滑动即可连续阅读漫画的阅读模式。这种模式适配手机和电脑，提供流畅的连续阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '漫画是否都是无删减版本？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，尾巴漫画所有作品均为无删减完整版，忠实还原韩国漫画原汁原味的内容。我们尊重创作者的原创表达，不对漫画内容进行修改或删减。',
        },
      },
    ],
  };

  return (
    <>
      <PageMeta
        title="关于我们 - 尾巴漫画 | 品牌介绍与联系方式"
        description="尾巴漫画是专业的韩国漫画在线阅读平台，提供无删减、下拉式免费韩漫。了解我们的品牌故事、用户口碑与联系方式。"
        keywords="尾巴漫画,关于我们,韩国漫画,免费韩漫,品牌介绍"
        canonicalUrl="/about"
        jsonLd={{ ...orgJsonLd, ...faqJsonLd }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">关于尾巴漫画</h1>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
              尾巴漫画是一个专注于韩国漫画在线阅读的平台，致力于为广大韩漫爱好者提供无删减、高质量的漫画阅读体验。
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-14">
            <div className="text-center p-8 rounded-lg bg-background-100 border border-background-200/70">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-primary-100">
                <i className="ri-heart-line text-3xl text-primary-500 w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-950 mb-2">热爱漫画</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">我们是一群热爱韩国漫画的伙伴，因为共同的兴趣聚在一起，希望把优质的韩漫分享给更多读者。</p>
            </div>
            <div className="text-center p-8 rounded-lg bg-background-100 border border-background-200/70">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-accent-100">
                <i className="ri-shield-check-line text-3xl text-accent-500 w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-950 mb-2">品质保证</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">所有漫画经过严格筛选，确保画质清晰、翻译准确、内容完整，为读者提供最佳的阅读体验。</p>
            </div>
            <div className="text-center p-8 rounded-lg bg-background-100 border border-background-200/70">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-secondary-100">
                <i className="ri-user-heart-line text-3xl text-secondary-500 w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-950 mb-2">用户至上</h3>
              <p className="text-sm text-foreground-500 leading-relaxed">我们重视每一位读者的反馈，持续优化阅读体验，努力成为您最信赖的韩漫阅读平台。</p>
            </div>
          </div>

          <section className="mb-14">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-8">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "漫画更新速度真的很快，画质也很清晰。下拉式阅读体验特别流畅，上班摸鱼的时候也能偷偷看几话哈哈。推荐给了好多朋友！"
                </p>
                <p className="text-xs text-foreground-400 font-medium">— 漫画迷小王</p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "终于找到无删减版了！之前在其他平台看的都是删减过的，很多精彩内容都错过了。尾巴漫画真的是良心平台，全免费还没有广告骚扰。"
                </p>
                <p className="text-xs text-foreground-400 font-medium">— 韩漫爱好者小李</p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i key={star} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "分类很清晰，找漫画特别方便。热门排行榜也很有参考价值，通过推荐发现了好多宝藏漫画。已经在这里追了十几部了，出不去了。"
                </p>
                <p className="text-xs text-foreground-400 font-medium">— 追漫达人小陈</p>
              </div>
            </div>
          </section>

          <section className="mb-14" id="faq">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-8">常见问题</h2>
            <div className="max-w-2xl mx-auto space-y-4">
              {[
                { q: '尾巴漫画上的漫画是否完全免费？', a: '是的，尾巴漫画上的所有韩国漫画均为完全免费阅读，无需付费订阅或购买。我们致力于为韩漫爱好者提供便捷的免费阅读体验。' },
                { q: '漫画更新频率是怎样的？', a: '热门连载漫画通常在韩国更新后24小时内同步上线，确保读者能够第一时间阅读到最新内容。具体更新频率因作品而异，连载作品一般每周更新一话。' },
                { q: '什么是下拉式阅读？', a: '下拉式阅读是指无需翻页，只需向下滑动即可连续阅读漫画的阅读模式。这种模式适配手机和电脑，提供流畅的连续阅读体验。' },
                { q: '漫画是否都是无删减版本？', a: '是的，尾巴漫画所有作品均为无删减完整版，忠实还原韩国漫画原汁原味的内容。我们尊重创作者的原创表达，不对漫画内容进行修改或删减。' },
              ].map((faq, i) => (
                <details key={i} className="group rounded-lg bg-background-100 border border-background-200/70">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer">
                    <h4 className="text-sm font-semibold text-foreground-950 pr-4">{faq.q}</h4>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 w-5 h-5 flex items-center justify-center shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section id="contact">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 text-center mb-8">联系我们</h2>
            <div className="max-w-xl mx-auto">
              <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute left-[-9999px] opacity-0 w-0 h-0 overflow-hidden pointer-events-none"
                />
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                    placeholder="请输入您的邮箱地址"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>
                <button
                  type="submit"
                  className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <div className="p-4 rounded-lg bg-accent-100 text-accent-700 text-sm text-center">
                    感谢您的留言！我们会尽快回复您。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="p-4 rounded-lg bg-primary-100 text-primary-700 text-sm text-center">
                    {formError}
                  </div>
                )}
              </form>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-100 shrink-0">
                    <i className="ri-mail-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">电子邮箱</p>
                    <p className="text-sm text-foreground-500">contact@weibamanga.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-accent-100 shrink-0">
                    <i className="ri-customer-service-2-line text-accent-500 w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">在线客服</p>
                    <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}